import React from 'react';
import { clsx } from 'clsx';
import { useNavigate, useSearch } from '@tanstack/react-router';

/**
 * Top-level mode switch for the Trades route: "Trades" and "History".
 *
 * Uses the established top-level tab style (mirrors the inventory TabNavigationBar:
 * `text-lg font-semibold`, blue-underline active) — these are PRIMARY page tabs.
 * The History subtabs deliberately use a DIFFERENT, nested folder-tab style so the
 * hierarchy reads as mode-tabs > subtabs.
 *
 * History → `?mode=history`; Trades → no `mode`. History (LR-376) surfaces terminal
 * positions (closed / cancelled). `cp` is reset on any mode change since it is
 * counterparty-scoped and only History reads it here.
 *
 * LR-672: the counterparty TAB (`?counterparty=`) and the saved view (`?view_id=`,
 * LR-671) are the section's scope, orthogonal to the mode — they are carried
 * across the switch so Trades ↔ History keeps the selected tab and view.
 *
 * LR-587 removed the third tab: Mark to Market moved to Collateral Manager, which
 * product answer #4 made its home (GMSLA MTM, and the repo margin call under Cash
 * Pools -> GMRA). `/trades?mode=mtm` redirects there, so old links still land.
 */
const TAB_BUTTON_CLASSES =
  'px-3 py-1.5 text-lg font-semibold transition-colors whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

export type TradesMode = 'trades' | 'history';

export interface TradesModeTabsProps {
  activeMode: TradesMode;
}

export const TradesModeTabs: React.FC<TradesModeTabsProps> = ({ activeMode }) => {
  const navigate = useNavigate();
  // Typed route search (not `prev` — it widens to the router-wide union): the tab
  // slug and the saved view are carried across the mode switch (LR-672).
  const { counterparty, view_id } = useSearch({ from: '/trades' });

  const go = (mode: TradesMode): void => {
    if (mode === activeMode) return;
    // Reset cp on any mode switch: it scopes a counterparty view and does not
    // carry across modes.
    const searchMode = mode === 'history' ? ('history' as const) : undefined;
    navigate({
      to: '/trades',
      // Reset htab too: it is History-scoped, and entering History should start on the
      // default (Trades) subtab (LR-390). LR-498: hview/sel are History-master–detail
      // scoped — reset them on any mode switch for the same reason.
      search: {
        mode: searchMode,
        cp: undefined,
        htab: undefined,
        hview: undefined,
        sel: undefined,
        counterparty,
        view_id,
      },
    });
  };

  const labelFor = (mode: TradesMode): string =>
    mode === 'trades' ? 'Trades' : 'History';

  return (
    <div
      data-tour="trades-mode-tabs"
      className="flex items-center gap-1 border-b-2 border-transparent"
      role="tablist"
      aria-label="Trades mode"
    >
      {(['trades', 'history'] as const).map((mode) => {
        const active = mode === activeMode;
        return (
          <button
            key={mode}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => go(mode)}
            className={clsx(
              TAB_BUTTON_CLASSES,
              active
                ? 'text-gray-900 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700',
            )}
          >
            {labelFor(mode)}
          </button>
        );
      })}
    </div>
  );
};
